<template>
  <div class="box-input-flight">
    <div>
      <div
        class="box-flights"
        v-bind:class="{ 'is-invalid': validation.$error }"
      >
        <base-input
          v-model="flightNumber"
          label="Flight N°"
          placeholder="Flight N°"
          id="flightNumber"
          name="flightNumber"
          inputClasses="input-group-button"
          format="uppercase"
          :disabled="loading"
          class="mb-0"
          type="search"
          @input="handleFlightNumberChanges"
        />
        <base-button
          type="outline-secondary"
          class="btn-group-input validate-button"
          :disabled="loading || !flightNumber"
          @click="loadScheduledFlights()"
        >
          <div v-if="loading" class="d-flex align-items-center">
            <img
              src="~rhomtrip_assets/src/img/icons/loaders/loader-1.gif"
              alt="loader"
              class="d-inline mr-2"
            />
            <span class="d-inline">Validating</span>
          </div>
          <span v-else>Validate</span>
        </base-button>
      </div>
      <span class="invalid-feedback d-block" v-if="validation.$error">
        {{ getErrorMessage(validation) }}
      </span>
    </div>
    <fade-transition v-if="showFlightsList" :duration="300" mode="out-in">
      <div class="dropListFlights">
        <div
          class="box-flight"
          v-for="(flight, index) in flights"
          :key="index"
          @click="
            handlerFlightSelected(
              flight,
              flightType,
              airportIataCode,
              selectFlight
            )
          "
        >
          <div class="row">
            <div class="col-6">
              <div class="content-flight">
                <span class="label-from-to">Departure</span>
                <span class="airport-code">
                  {{ flight.origin.airport_code }}
                </span>
                <span class="location">{{ flight.origin.location }}</span>
                <span class="flight-time">
                  {{ formatDate(flight.departuretime, flight.origin.timezone) }}
                  <br />
                  {{ formatTime(flight.departuretime, flight.origin.timezone) }}
                </span>
              </div>
            </div>
            <div class="col-6 border-left-dashed">
              <div class="content-flight">
                <span class="label-from-to">Arrival</span>
                <span class="airport-code">
                  {{ flight.destination.airport_code }}
                </span>
                <span class="location">{{ flight.destination.location }}</span>
                <span class="flight-time">
                  {{
                    formatDate(flight.arrivaltime, flight.destination.timezone)
                  }}
                  <br />
                  {{
                    formatTime(flight.arrivaltime, flight.destination.timezone)
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </fade-transition>
  </div>
</template>

<script>
import { FadeTransition } from "vue2-transitions";
import endpoint from "../../../Commons/Services/index";
import Swal from "sweetalert2";
import {
  filterUniqueFlights,
  formatDate,
  formatTime,
  generateMessageToFlightAdjustment,
  generateMessageToNotify,
  generateValidateAirportMessage,
  getPickupDate,
  getPickupTime
} from "../../services/flight.service";

export default {
  name: "FlightNumberInput",
  props: {
    value: {
      type: String,
      required: true,
      descriptions: "Flight number input value"
    },
    pickupDate: {
      type: String,
      required: true,
      descriptions: "Format YYYY-MM-DD"
    },
    pickupTime: {
      type: String,
      required: true,
      descriptions: "Format HH:mm A"
    },
    flightType: {
      type: String,
      enum: ["ARRIVAL", "DEPARTURE", "NONE"],
      required: false,
      default: "ARRIVAL",
      descriptions: "Type of flight to validate"
    },
    airport: {
      type: [Object, null],
      default: () => null,
      descriptions: "Airport object to validate against"
    },
    airportName: {
      type: String,
      required: false,
      default: "",
      descriptions: "Name of the airport for notifications"
    },
    validation: {
      type: Object,
      required: true,
      descriptions: "Vuelidate validation object for flight number"
    }
  },
  data() {
    return {
      // Flight number input value
      flightNumber: "",
      // Last validated flight number
      lastFlightNumber: "",
      // List of available flights
      flights: [],
      // Loading state
      loading: false,
      // Show flights list
      showFlightsList: false,
      // Flight number validation state
      isValidateFlightNumber: true
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.flightNumber = newVal;
        if (this.isValidateFlightNumber && newVal) {
          this.lastFlightNumber = newVal;
        }
      },
      deep: true,
      immediate: true
    }
  },
  created() {
    // Emit validation state to parent component
    this.$emit("validation-state", {
      isValidateFlightNumber: this.isValidateFlightNumber,
      flightType: this.flightType
    });
  },
  methods: {
    formatDate,
    formatTime,
    getPickupDate,
    getPickupTime,
    generateMessageToFlightAdjustment,
    generateMessageToNotify,
    /**
     * Handles changes to the flight number input.
     * Emits the new value and updates validation state.
     * @param {string} flightNumber - The new flight number input value.
     */
    handleFlightNumberChanges(flightNumber) {
      // Reset flights list and validation state on input change
      this.$emit("input", flightNumber);
      this.isValidateFlightNumber =
        flightNumber === this.lastFlightNumber || !flightNumber;

      // Emit validation state to parent component
      this.$emit("validation-state", {
        isValidateFlightNumber: this.isValidateFlightNumber,
        flightType: this.flightType
      });

      if (!flightNumber) {
        this.showFlightsList = false;
      }
    },
    /**
     * Handles flight selection and verifies if it matches the expected airport.
     * Notifies the user if the airport does not match.
     * @param {Object} flight - The selected flight object.
     * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE).
     * @param {Object} airport - The expected airport object containing IATA code.
     * @param {Function} callback - The callback function to execute if the flight is valid.
     */
    async handlerFlightSelected(flight, flightType, airportIataCode, callback) {
      const isCorrectAirport = this.verifyCorrectAirport(
        flight,
        flightType,
        airportIataCode
      );
      if (!isCorrectAirport) {
        this.$notify({
          type: "warning",
          title: "Sorry!",
          message: generateValidateAirportMessage(flightType),
          icon: "flaticon-remove"
        });
        return;
      }

      await callback(flight, flightType);
    },
    /**
     * Prompts user confirmation and selects the flight if confirmed.
     * Adjusts pickup date/time if necessary.
     * @param {Object} flight - The selected flight object.
     * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE).
     */
    async selectFlight(flight, flightType) {
      const date =
        flightType === "ARRIVAL" ? flight.arrivaltime : flight.departuretime;
      const timezone =
        flightType === "ARRIVAL"
          ? flight.destination.timezone
          : flight.origin.timezone;

      const pickupDate = this.getPickupDate(date, timezone);
      const pickupTime = this.getPickupTime(date, timezone);

      if (pickupDate === this.pickupDate && pickupTime === this.pickupTime) {
        await this.applyFlightSelection(flight, flightType, false);
      } else {
        await this.confirmFlightAdjustment(flight, flightType);
      }
    },
    /**
     * Shows confirmation dialog for flight adjustment.
     * @param {Object} flight - The selected flight object.
     * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE).
     */
    async confirmFlightAdjustment(flight, flightType) {
      let { value } = await Swal.fire({
        title: "Confirm Flight Adjustment",
        html: `${this.generateMessageToFlightAdjustment(
          flightType
        )} <br><br><strong>Do you want to proceed?</strong>`,
        imageUrl: require("rhomtrip_assets/src/img/icons/alerts/warning.png"),
        buttonsStyling: false,
        allowOutsideClick: false,
        showCancelButton: true,
        focusConfirm: false,
        reverseButtons: true,
        cancelButtonText: "No",
        confirmButtonText: "Yes",
        confirmButtonClass: "btn btn-primary btn-small-one"
      });
      let isAdjustingDateTime = false;
      if (value) {
        isAdjustingDateTime = true;
      }
      await this.applyFlightSelection(flight, flightType, isAdjustingDateTime);
    },
    /**
     * Applies the selected flight and emits the selection event.
     * @param {Object} flight - The selected flight object.
     * @param {string} flightType - The type of flight.
     * @param {boolean} isAdjustingDateTime - Flag indicating if date/time adjustment is needed.
     */
    async applyFlightSelection(flight, flightType, isAdjustingDateTime) {
      this.showFlightsList = false;
      this.isValidateFlightNumber = true;
      if (this.isValidateFlightNumber) {
        this.lastFlightNumber = this.flightNumber;
      }
      // Emit validation state to parent component
      this.$emit("validation-state", {
        isValidateFlightNumber: this.isValidateFlightNumber,
        flightType: this.flightType
      });
      // Emit the selected flight details to the parent component
      this.$emit("selectFlight", {
        flight,
        flightType,
        isAdjustingDateTime: isAdjustingDateTime
      });
    },
    /**
     * Loads scheduled flights based on the entered flight number and pickup date.
     * Notifies the user if no flights are found.
     */
    async loadScheduledFlights() {
      if (this.validation.isFlightValid && this.validation.$model) {
        try {
          this.loading = true;
          this.showFlightsList = false;
          let flights = await this.fetchScheduledFlights(
            this.flightNumber.trim().toUpperCase(),
            this.pickupDate
          );
          this.loading = false;
          if (flights.length > 0) {
            this.flights = filterUniqueFlights(flights);
            this.showFlightsList = true;
          } else {
            this.$notify({
              type: "danger",
              title: "Sorry!",
              message:
                "We couldn’t find a flight matching your number and travel time. Please double-check your flight number or pickup time.",
              icon: "flaticon-remove"
            });
          }
        } catch (error) {
          console.error("Error loading scheduled flights:", error);
          this.$notify({
            type: "danger",
            title: "Sorry!",
            message:
              "We couldn’t find a flight matching your number and travel time. Please double-check your flight number or pickup time.",
            icon: "flaticon-remove"
          });
        }
      } else {
        this.validation.$touch();
      }
    },
    /**
     * Verifies if the flight's airport matches the expected airport based on flight type.
     * @param {Object} flight - The flight object containing origin and destination details.
     * @param {string} flightType - The type of flight (ARRIVAL or DEPARTURE).
     * @param {Object} airport - The expected airport object containing IATA code.
     * @returns {boolean} True if the airport matches, false otherwise.
     */
    verifyCorrectAirport(flight, flightType, airportIataCode) {
      if (flightType === "ARRIVAL" && airportIataCode) {
        return flight.destination.airport_code === airportIataCode;
      } else if (flightType === "DEPARTURE" && airportIataCode) {
        return flight.origin.airport_code === airportIataCode;
      }
      return true;
    },
    /**
     * Fetches scheduled flights based on flight number and date.
     * @param {string} flightNumber - The flight number to search for.
     * @param {string} date - The date to search for flights (format: YYYY-MM-DD).
     * @returns {Promise<Array>} A promise that resolves to an array of scheduled flights.
     */
    async fetchScheduledFlights(flightNumber, date) {
      try {
        let { data } = await endpoint.flights.services.fetchScheduledFlights(
          flightNumber,
          date
        );
        if (data.data) {
          return data.data.flights || [];
        }
        return [];
      } catch (error) {
        console.error("Error fetching scheduled flights:", error);
        return [];
      }
    },
    /**
     * Gets the appropriate error message for flight number validation.
     * @param {Object} flightNumber - The flight number validation object.
     * @returns {string} The error message.
     */
    getErrorMessage(flightNumber) {
      if (flightNumber.$error) {
        if (!flightNumber.isFlightValid) {
          return "Enter a valid Flight N°.";
        } else if (!flightNumber.required) {
          return "Enter a Flight N°.";
        } else if (!flightNumber.isValidate) {
          return "Validate a Flight N°";
        }
      }
      return "";
    }
  },
  computed: {
    /**
     * Computes the IATA code of the airport based on the provided airport object or name.
     * @returns {string} The IATA code of the airport.
     */
    airportIataCode() {
      if (this.airport && this.airport.iata) {
        return this.airport.iata;
      }

      let [name, data] = this.airportName.split(" | ");
      if (name && data) {
        let [iataCode, location, country] = data.split(", ");
        return iataCode ? iataCode.trim() : "";
      }
      return "";
    }
  },
  components: {
    FadeTransition
  }
};
</script>
